<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  useLookupFetcher,
  FilterDefinition,
  FilterType,
  FilterTypeEnum,
  useApplyFilter,
  formatOptionLabel
} from "@/features/filters";
import { useFiltersStore } from "@/store";
import { McTooltip } from "@maersk-global/mds-components-core";
import { normalisedText } from "@/global";
import { isFilterDisabled } from "@/features/filters/utils/isFilterDisabled";
import "@maersk-global/mds-components-core-multi-select";
import "@maersk-global/mds-components-core-option";

interface MultiSelectElement extends HTMLElement {
  value?: { label: string; value: string }[];
}
const props = withDefaults(
  defineProps<{
    filter: FilterDefinition;
    filterType: FilterType;
    zonePrefix?: string;
  }>(),
  {
    zonePrefix: undefined
  }
);

const {
  key: filterKey,
  label: filterLabel = "",
  hiddenlabel,
  placeholder = "",
  groupLabel,
  groupId,
  sectionLabel,
  options: optionsProp,
  lookupUrl,
  config,
  dependencies,
  labelTooltip = false,
  inputParams,
  disableOnOtherFilters
} = props.filter;

const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const normalizeSelectedFilterValues = (
  storeValue: any,
  availableOptions: { label: string; value: string }[]
): { label: string; value: string }[] => {
  let rawValues: { label: string; value: string }[] = [];

  if (Array.isArray(storeValue)) {
    rawValues = storeValue.map((item: any) =>
      typeof item === "object" && item.value !== undefined ? item : { label: String(item), value: item }
    );
  } else if (storeValue && typeof storeValue === "object" && storeValue.value) {
    const values = Array.isArray(storeValue.value) ? storeValue.value : [storeValue.value];
    rawValues = values.map((val: any) =>
      typeof val === "object" && val.value !== undefined ? val : { label: String(val), value: val }
    );
  } else if (storeValue) {
    rawValues = [{ label: String(storeValue), value: storeValue }];
  }

  // Match to existing options by value to maintain reference
  return rawValues.map((item) => {
    const match = availableOptions.find((opt) => opt.value === item.value);
    return match || item;
  });
};

const hasStaticOptions = !!optionsProp;
const hasLoaded = ref(false);

const resolvedDependencies = computed(() => {
  const deps: Record<string, any> = {};
  if (!dependencies) return deps;

  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;

  for (const [param, dependsOn] of Object.entries(dependencies)) {
    const dep = source?.[zone]?.[dependsOn];
    deps[param] = typeof dep === "object" ? dep?.value : dep;
  }
  return deps;
});

const {
  options: fetchedOptions,
  fetch,
  loading
} = useLookupFetcher(
  filterKey,
  lookupUrl || "",
  config || {},
  { forceRefresh: false, autoFetchEnabled: false },
  resolvedDependencies,
  inputParams
);

const options = computed(() => {
  const rawOptions = optionsProp || fetchedOptions.value;
  const formatTemplate = props.filter.optionLabelFormat;
  const normaliseText = props.filter.normaliseText;
  const subLabelKey = props.filter.subLabelKey;

  if (!formatTemplate && !normaliseText)
    return rawOptions.map((opt) => ({ ...opt, ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}) }));

  return rawOptions.map((opt) => ({
    ...opt,
    ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}),
    ...(formatTemplate && { label: formatOptionLabel(opt, formatTemplate) || opt.label }),
    ...(normaliseText && { label: normalisedText(opt.label) || opt.label })
  }));
});

const optionIconOf = (option: { value: string | number }) => props.filter.optionIcon?.(option.value);

const selectedLabels = computed(() => {
  return localSelectedFilters.value.map((f) => f.label);
});

const hintText = computed(() => {
  if (!selectedLabels.value.length) return "";
  return `Selected: ${selectedLabels.value.join(", ")}`;
});
const isDisabled = computed(() => {
  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return isFilterDisabled(props.filter.disabled, disableOnOtherFilters, source?.[zone]);
});

const onOpen = () => {
  if (!hasStaticOptions && !hasLoaded.value) {
    fetch();
    hasLoaded.value = true;
  }
};

const getValidSelectedValues = (currentValues: { label: string; value: string }[]) => {
  if (!Array.isArray(currentValues)) return [];
  return currentValues.filter(
    (item) => item && typeof item.value === "string" && options.value.some((option) => option.value === item.value)
  );
};

const createFilterPayload = (validValues: { label: string; value: string }[]) => {
  return {
    filterKey,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {}),
    filterType: props.filterType,
    label: validValues.map((v) => v.label).join(", "),
    value: validValues
  };
};

const handleInvalidSelections = (validValues: { label: string; value: string }[]) => {
  if (!Array.isArray(validValues) || validValues.length === 0) {
    removeFilter(filterKey);
    return;
  }

  const payload = createFilterPayload(validValues);
  applyFilter(filterKey, payload);
};

// Local reactive ref for selections bound to UI
const localSelectedFilters = ref<{ label: string; value: string }[]>([]);

// Flag to prevent auto-resetting after manual clear
const userCleared = ref(false);

const initializeSelectedFilters = () => {
  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeEntry = props.filter.notApplicable ? undefined : source?.[zone]?.[filterKey];
  if (storeEntry !== undefined && storeEntry !== null) {
    localSelectedFilters.value = normalizeSelectedFilterValues(storeEntry, options.value);
  }
};

// Run initialization on options load/change (options may affect normalization)
watch(
  options,
  () => {
    initializeSelectedFilters();
  },
  { immediate: true }
);

// Sync localSelectedFilters if store changes (unless user just cleared)
watch(
  () => {
    const zone = props.zonePrefix || "default";
    const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
    return props.filter.notApplicable ? undefined : source?.[zone]?.[filterKey];
  },
  (newVal) => {
    if (userCleared.value) {
      queueMicrotask(() => {
        userCleared.value = false;
      });
      return;
    }

    if (newVal === undefined || newVal === null) {
      localSelectedFilters.value = [];
    } else {
      localSelectedFilters.value = normalizeSelectedFilterValues(newVal, options.value);
    }
  },
  { deep: true, immediate: true }
);

// On user changing selection
const onChange = (event: Event) => {
  userCleared.value = false; // user actively selecting

  const multiSelect = event.target as MultiSelectElement;
  const selectedValues = multiSelect?.value || [];

  const validValues = selectedValues.map((val) => {
    const matched = options.value.find((opt) => opt.value === (val?.value ?? val));
    if (matched) return matched;
    if (val?.label !== undefined && val?.value !== undefined) return val;
    return { label: String(val), value: val };
  });

  localSelectedFilters.value = validValues;

  if (validValues.length === 0 && props.filter.isHotUpdate) {
    removeFilter(filterKey);
    return;
  }

  applyFilter(filterKey, createFilterPayload(validValues));
};

// On clear button click
const onClear = () => {
  userCleared.value = true;
  localSelectedFilters.value = [];
  if (props.filter.isHotUpdate) removeFilter(filterKey);
};

const validateCurrentSelections = () => {
  const current = localSelectedFilters.value;

  if (current.length === 0) {
    return;
  }

  const validValues = getValidSelectedValues(current);
  const hasInvalidSelections = validValues.length !== current.length;

  if (hasInvalidSelections) {
    handleInvalidSelections(validValues);
  }
};

watch(
  resolvedDependencies,
  () => {
    hasLoaded.value = false;
    validateCurrentSelections();
  },
  { deep: true }
);
</script>

<template>
  <div :style="{ width: props.filter.width, minWidth: props.filter.width }">
    <mc-tooltip fit="small">
      <mc-multi-select
        slot="trigger"
        :name="filterKey"
        fit="small"
        :label="t(filterLabel)"
        :hiddenlabel="hiddenlabel"
        :placeholder="t(placeholder)"
        :loading="!hasStaticOptions && loading"
        :data-test="`filter-text-${filterKey}`"
        :value="localSelectedFilters"
        :disabled="isDisabled"
        @input="onChange"
        @focus="onOpen"
        @click="onOpen"
        @clearbuttonclick="onClear"
      >
        <span v-if="hintText" slot="hint">{{ hintText }}</span>
        <mc-option
          v-for="option in options"
          :key="option.value"
          :value="option"
          :icon="optionIconOf(option)?.icon"
          :data-test="optionIconOf(option) ? 'multi-select-option-icon' : undefined"
        >
          {{ option.label }}
        </mc-option>
      </mc-multi-select>

      <span v-if="labelTooltip && localSelectedFilters?.length">
        {{ t(filterLabel) }}
      </span>
    </mc-tooltip>
  </div>
</template>
